import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Briefcase, ArrowLeft, Loader2, CheckCircle2, XCircle, Clock,
  Send, RotateCcw, User, Calendar, DollarSign, FileText, AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SESView = () => {
  const navigate = useNavigate();
  const { entry_sheet_number, fiscal_year } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [entry, setEntry] = useState(null);
  const [acceptanceMode, setAcceptanceMode] = useState(false);
  const [acceptanceItems, setAcceptanceItems] = useState([]);


  const loadEntry = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/service-entries/${entry_sheet_number}/${fiscal_year}`);
      setEntry(response.data);
      
      // Initialize acceptance items
      setAcceptanceItems(response.data.items.map(item => ({
        item_number: item.item_number,
        quantity_accepted: item.quantity_accepted || item.quantity,
        acceptance_status: item.acceptance_status || 'ACCEPTED',
        rejection_reason: ''
      })));
    } catch (error) {
      console.error('Error loading entry:', error);
      toast.error('Failed to load service entry');
      navigate('/p2p/ses-list');
    } finally {
      setLoading(false);
    }
  }, [entry_sheet_number, fiscal_year, navigate]);

  useEffect(() => {
    loadEntry();
  }, [loadEntry]);

  const handleAccept = async () => {
    try {
      setActionLoading(true);
      
      const payload = {
        items: acceptanceItems,
        accepted_by: 'ADMIN',
        comments: ''
      };
      
      await axios.post(
        `${BACKEND_URL}/api/service-entries/${entry_sheet_number}/${fiscal_year}/accept`,
        payload
      );
      
      toast.success('Service entry accepted successfully!');
      setAcceptanceMode(false);
      loadEntry();
    } catch (error) {
      console.error('Error accepting entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept service entry');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this service entry?')) return;
    
    try {
      setActionLoading(true);
      await axios.post(`${BACKEND_URL}/api/service-entries/${entry_sheet_number}/${fiscal_year}/cancel`);
      toast.success('Service entry cancelled');
      loadEntry();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel entry');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateInvoice = () => {
    navigate(`/p2p/ir-create?ses=${entry_sheet_number}`);
  };

  const updateAcceptanceItem = (index, field, value) => {
    const newItems = [...acceptanceItems];
    newItems[index][field] = value;
    setAcceptanceItems(newItems);
  };

  const getStatusColor = (status) => {
    const colors = {
      'CREATED': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'PARTIALLY_ACCEPTED': 'bg-orange-100 text-orange-800 border-orange-200',
      'ACCEPTED': 'bg-green-100 text-green-800 border-green-200',
      'REJECTED': 'bg-red-100 text-red-800 border-red-200',
      'INVOICED': 'bg-orange-100 text-orange-800 border-orange-200',
      'CANCELLED': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CREATED': return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'ACCEPTED': return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'PARTIALLY_ACCEPTED': return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'REJECTED': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'INVOICED': return <FileText className="w-5 h-5 text-orange-600" />;
      default: return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading service entry...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!entry) return null;

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/p2p/ses-list')}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Briefcase className="w-7 h-7 text-orange-600" />
                Service Entry: {entry.entry_sheet_number}
              </h1>
              <p className="text-sm text-gray-500">
                Fiscal Year: {entry.fiscal_year} | PO: {entry.po_number}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {entry.status === 'CREATED' && !acceptanceMode && (
              <Button onClick={() => setAcceptanceMode(true)} className="bg-green-600 hover:bg-green-700">
                <CheckCircle2 className="w-4 h-4 mr-2" />Accept
              </Button>
            )}
            {entry.status === 'ACCEPTED' && (
              <Button onClick={handleCreateInvoice} className="bg-orange-600 hover:bg-orange-700">
                <FileText className="w-4 h-4 mr-2" />Create Invoice
              </Button>
            )}
            {entry.status !== 'CANCELLED' && entry.status !== 'INVOICED' && (
              <Button variant="outline" onClick={handleCancel} disabled={actionLoading} className="text-red-600">
                <RotateCcw className="w-4 h-4 mr-2" />Cancel
              </Button>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex items-center gap-3 mb-6">
          <span className={`px-4 py-2 rounded-full text-sm font-medium border flex items-center gap-2 ${getStatusColor(entry.status)}`}>
            {getStatusIcon(entry.status)}
            Status: {entry.status}
          </span>
          <span className="px-4 py-2 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
            Acceptance: {(entry.acceptance_percentage || 0).toFixed(0)}%
          </span>
        </div>

        {/* Summary Card */}
        <div className="bg-gradient-to-r from-orange-50 to-pink-50 rounded-lg border border-orange-200 p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-orange-600">Vendor</p>
              <p className="font-semibold text-orange-900">{entry.vendor_id}</p>
              <p className="text-xs text-orange-700">{entry.vendor_name}</p>
            </div>
            <div>
              <p className="text-xs text-orange-600 flex items-center gap-1"><Calendar className="w-3 h-3" />Entry Date</p>
              <p className="font-semibold text-orange-900">{entry.entry_date}</p>
            </div>
            <div>
              <p className="text-xs text-orange-600">Service Period</p>
              <p className="font-semibold text-orange-900">
                {entry.service_period_start} - {entry.service_period_end}
              </p>
            </div>
            <div>
              <p className="text-xs text-orange-600 flex items-center gap-1"><User className="w-3 h-3" />Created By</p>
              <p className="font-semibold text-orange-900">{entry.created_by}</p>
            </div>
            <div>
              <p className="text-xs text-orange-600">External Ref</p>
              <p className="font-semibold text-orange-900">{entry.external_reference || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-orange-600">Items</p>
              <p className="font-semibold text-orange-900">{entry.items?.length || 0}</p>
            </div>
          </div>
        </div>

        {/* Acceptance Mode */}
        {acceptanceMode && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Accept Service Entry Items
            </h2>
            <p className="text-sm text-green-700 mb-4">
              Review and accept the service quantities below. You can partially accept or reject items.
            </p>
            
            <div className="space-y-4">
              {entry.items.map((item, index) => (
                <div key={item.item_number} className="bg-white p-4 rounded-md border">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-medium text-gray-900">Item {item.item_number}: {item.short_text}</p>
                      <p className="text-sm text-gray-500">Entered Qty: {item.quantity} {item.unit_of_measure}</p>
                    </div>
                    <select
                      value={acceptanceItems[index]?.acceptance_status || 'ACCEPTED'}
                      onChange={(e) => updateAcceptanceItem(index, 'acceptance_status', e.target.value)}
                      className="px-3 py-1 border rounded-md text-sm"
                    >
                      <option value="ACCEPTED">Accept</option>
                      <option value="PARTIALLY_ACCEPTED">Partial Accept</option>
                      <option value="REJECTED">Reject</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Quantity to Accept</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={acceptanceItems[index]?.quantity_accepted || 0}
                        onChange={(e) => updateAcceptanceItem(index, 'quantity_accepted', parseFloat(e.target.value))}
                        max={item.quantity}
                        disabled={acceptanceItems[index]?.acceptance_status === 'REJECTED'}
                      />
                    </div>
                    {acceptanceItems[index]?.acceptance_status === 'REJECTED' && (
                      <div>
                        <Label className="text-xs">Rejection Reason</Label>
                        <Input
                          value={acceptanceItems[index]?.rejection_reason || ''}
                          onChange={(e) => updateAcceptanceItem(index, 'rejection_reason', e.target.value)}
                          placeholder="Reason for rejection"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setAcceptanceMode(false)}>
                Cancel
              </Button>
              <Button onClick={handleAccept} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
                {actionLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-2" />Confirm Acceptance</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Entry Totals */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Entry Totals
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <p className="text-xs text-orange-600">Qty Entered</p>
              <p className="text-xl font-bold text-orange-900">{(entry.total_quantity_entered || 0).toFixed(2)}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-xs text-green-600">Qty Accepted</p>
              <p className="text-xl font-bold text-green-900">{(entry.total_quantity_accepted || 0).toFixed(2)}</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <p className="text-xs text-orange-600">Net Value</p>
              <p className="text-xl font-bold text-orange-900">
                {entry.currency} {(entry.total_net_value || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
              </p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <p className="text-xs text-orange-600">Gross Value</p>
              <p className="text-xl font-bold text-orange-900">
                {entry.currency} {(entry.total_gross_value || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
              </p>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Entry Items</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Item</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Accepted</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Value</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {entry.items?.map((item) => (
                  <tr key={item.item_number} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-orange-600">{item.item_number}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{item.short_text}</p>
                      {item.personnel_name && (
                        <p className="text-xs text-gray-500">By: {item.personnel_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.po_item}</td>
                    <td className="px-4 py-3 text-right">
                      <p className="font-medium">{(item.quantity || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">{item.unit_of_measure}</p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <p className={`font-medium ${item.quantity_accepted > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {(item.quantity_accepted || 0).toFixed(2)}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {item.currency} {(item.gross_price || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {item.currency} {(item.net_value || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        item.acceptance_status === 'ACCEPTED' ? 'bg-green-100 text-green-800' :
                        item.acceptance_status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                        item.acceptance_status === 'PARTIALLY_ACCEPTED' ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {item.acceptance_status || 'PENDING'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes */}
        {entry.header_text && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Notes</h2>
            <p className="text-gray-700">{entry.header_text}</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SESView;
